<template>
  <v-card>
    <v-card-title class="py-2">
      <v-icon class="mr-2">merge_type</v-icon>
      <div class="title info--text">Aliases</div>
      <div class="title pl-1"> ⋅ </div>
      <div class="caption pl-1"> {{ aliases.length }} {{ pluralize('alias', aliases.length, 'es') }}</div>
    </v-card-title>
    <v-divider />
    <template v-if="editForm">
      <v-card-actions>
        <v-btn color="success" small @click="showAddAliaasDialog" :disabled="makePrimaryIsLoading">
          <v-icon class="mr-1">add</v-icon>
          Add
        </v-btn>
        <v-btn color="warning" small @click="showMergeAliasDialog" :disabled="makePrimaryIsLoading">
          <v-icon class="mr-1">merge_type</v-icon>
          Merge Existing
        </v-btn>
      </v-card-actions>
    </template>
    <v-card-text>
      <p>
        <strong>Name aliases</strong> are alternative names for this data source.
      </p>
    </v-card-text>
    <v-data-table hide-actions :items="aliases" no-data-text="No aliases" hide-headers>
      <template v-slot:items="{ item }">
        <td class="text-xs-left">
          <v-chip label small> {{ item.Name }}</v-chip>
        </td>
        <td class="text-xs-right">
          <span v-if="item.IsPrimary" class="caption info--text font-weight-">Primary
            <Tooltip small>
              <div>The primary name shows up in reports for this data source.</div>
              <div v-if="primaryAliasNameDifferentFromDataSourceName">
                <strong>Note:</strong> The primary alias is currently different than the location name.
              </div>
            </Tooltip>
          </span>
          <div v-show="editForm && !item.IsPrimary">
            <v-btn v-if="isSystemAdmin" color="info" small :loading="item.IsLoading" :disabled="makePrimaryIsLoading" @click="makePrimary(item)">
              <v-icon class="mr-2">admin_panel_settings</v-icon>
              <span class="text--capitalize">
                Make primary
              </span>
            </v-btn>
            <v-tooltip :open-delay="500" left>
              <template v-slot:activator="{on}">
                <v-btn v-show="editForm && !item.IsPrimary" v-on="on" icon :disabled="makePrimaryIsLoading" @click="showUnmergeAliasDialog(item.Name)">
                  <v-icon color="warning">call_split</v-icon>
                </v-btn>
              </template>
              <span>Separate alias from current data source</span>
            </v-tooltip>
          </div>
        </td>
      </template>
    </v-data-table>
    <v-divider></v-divider>
    <AddAliasDialog ref="add-alias-dialog" :dataSourceIndex="dataSourceIndex" />
    <MergeAliasDialog ref="merge-alias-dialog" :dataSourceIndex="dataSourceIndex" />
    <UnmergeAliasDialog ref="unmerge-alias-dialog" :dataSourceIndex="dataSourceIndex" />
  </v-card>
</template>
<script>
  import {
    pluralize,
  } from '@/helpers/strings'

  import {
    createNamespacedHelpers,
  } from 'vuex'
  const app = createNamespacedHelpers('app')
  const authenticate = createNamespacedHelpers('authenticate')
  const synonyms = createNamespacedHelpers('synonyms')
  const datasource = createNamespacedHelpers('datasource')
  const monitoringfeature = createNamespacedHelpers('monitoringfeature')

  import {
    SOURCE_TYPES,
  } from '@/store/MonitoringFeature/monitoringfeature'

  import AddAliasDialog from '@/components/MonitoringFeature/DataSources/VegetativeMonitoringEnviroData/Details/ManageAliases/AddAliasDialog'
  import MergeAliasDialog from '@/components/MonitoringFeature/DataSources/VegetativeMonitoringEnviroData/Details/ManageAliases/MergeAliasDialog'
  import UnmergeAliasDialog from '@/components/MonitoringFeature/DataSources/VegetativeMonitoringEnviroData/Details/ManageAliases/UnmergeAliasDialog'
  export default {
    components: {
      AddAliasDialog,
      MergeAliasDialog,
      UnmergeAliasDialog,
    },
    props: {
      editForm: Boolean,
      dataSourceIndex: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        aliases: [],
      }
    },
    computed: {
      ...authenticate.mapGetters(['isSystemAdmin']),
      ...monitoringfeature.mapGetters(['currentFeatureDataSourceIndexByType', 'currentMonitoringFeature']),
      // aliases() {
      //   return this.dataSource.Synonyms || []
      // },
      primaryAlias() {
        return this.aliases.find(x => x.IsPrimary)
      },
      primaryAliasNameDifferentFromDataSourceName() {
        if (this.primaryAlias == null || this.currentMonitoringFeature == null) {
          return false
        }
        return this.primaryAlias.Name != this.currentMonitoringFeature.DisplayName
      },
      makePrimaryIsLoading() {
        const everyAliasIsNotLoading = this.aliases.every(x => !x.IsLoading)
        return !everyAliasIsNotLoading
      },
    },
    watch: {
      dataSourceIndex() {
        this.aliases = this.createTableAliases()
      },
    },
    methods: {
      ...app.mapActions(['setSnackbar']),
      ...synonyms.mapActions(['updateSynonym']),
      ...datasource.mapActions(['fetchDataSources']),
      ...monitoringfeature.mapActions(['refreshCurrentMFDetails']),
      pluralize,
      showAddAliaasDialog() {
        this.$refs['add-alias-dialog'].show()
      },
      showMergeAliasDialog() {
        this.$refs['merge-alias-dialog'].show()
      },
      showUnmergeAliasDialog(synonymName) {
        this.$refs['unmerge-alias-dialog'].show(synonymName)
      },
      async makePrimary(synonym) {
        this.$set(synonym, 'IsLoading', true)
        try {
          const payload = {
            synonymId: synonym.Id,
            isPrimary: true,
            synonymName: synonym.Name,
            sourceType: SOURCE_TYPES.vegetativeMonitoringEnvirodata,
          }
          await this.updateSynonym(payload)

          await this.refresh()
          this.setSnackbar({
            text: `${synonym.Name} set as primary plot name`,
            color: 'success',
            icon: 'done',
          })

        } catch (err) {
          this.setSnackbar({
            text: err.Message || err,
            color: 'error',
            icon: 'error',
          })
        } finally {
          this.$set(synonym, 'IsLoading', false)
        }
      },
      createTableAliases() {
        if (!(this.dataSourceIndex && this.dataSourceIndex.Synonyms)) {
          return []
        }
        return this.dataSourceIndex.Synonyms.map(x => ({
          Id: x.Id,
          IsPrimary: x.IsPrimary,
          Name: x.Name,
          IsLoading: false,
        }))
      },
      async refresh() {
        const fetchIndicesPromise = this.fetchDataSources({
          sourceTypes: [SOURCE_TYPES.vegetativeMonitoringEnvirodata],
        })
        const fetchDetailsPromise = this.refreshCurrentMFDetails()
        await Promise.all([fetchIndicesPromise, fetchDetailsPromise])
      },
    },
    mounted() {
      this.aliases = this.createTableAliases()
    },
  }
</script>